import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db745iun6nb9f68e9950";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    formData.delete("website_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string") payload.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const code = (parsed?.code as string) ?? "";
      const serverMsg =
        (meta.message as string) ||
        (parsed?.message as string) ||
        (meta.detail as string) ||
        responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络连接后重试。");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";

  if (status === "success") {
    return (
      <div className="flex flex-col items-center rounded-lg border border-secondary-200 bg-secondary-50 px-6 py-12 text-center">
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-checkbox-circle-line text-3xl" />
        </span>
        <h3 className="text-lg font-bold text-secondary-900">反馈已提交，感谢你的来信</h3>
        <p className="mt-2 max-w-md text-sm text-secondary-800/80">
          我们会认真阅读每一条反馈。若涉及内容纠错或法律建议，编辑团队会尽快评估处理。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 rounded-md bg-secondary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-700"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="db745iun6nb9f68e9950"
      data-readdy-form="true"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            您的称呼 <span className="text-accent-600">*</span>
          </label>
          <input id="fb-name" name="name" type="text" required placeholder="请输入称呼" className={inputClass} />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input id="fb-email" name="email" type="email" required placeholder="用于接收回复" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select id="fb-topic" name="topic" defaultValue="内容纠错" className={inputClass}>
          <option value="内容纠错">内容纠错</option>
          <option value="法律建议">法律建议</option>
          <option value="伦理解读">伦理解读</option>
          <option value="合作与投稿">合作与投稿</option>
          <option value="其他反馈">其他反馈</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
          详细内容 <span className="text-accent-600">*</span>
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的问题或建议（不超过 500 字）"
          className={`${inputClass} resize-none`}
        />
        <p className="mt-1.5 text-xs text-foreground-400">最多 500 字，超出将无法提交。</p>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="extra-contact-line"
      />

      {status === "error" && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5 text-sm text-accent-800">
          <i className="ri-error-warning-line mt-0.5" />
          <span>{formError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            提交反馈
          </>
        )}
      </button>
      <p className="mt-3 text-xs text-foreground-400">提交即表示你了解本站内容仅供参考，不构成法律意见。</p>
    </form>
  );
}